// WaffleChart.jsx
import React from "react";
import "./WaffleChart.css";

const COLORS = [
  "#A81026",
  "#F3AC26",
  "#F15C41",
  "#6C6E2E",
  "#3B231E",
  "#4CBFAB",
  "#326AB3",
  "#29002D",
];

const NEUTRAL_COLOR = "#E0E0E0";
const TOTAL_CELLS = 100;

export default function WaffleChart({
  data = [],
  squareSize = 14,
  response_completion_summary,
  department_response_completion_summary
}) {
  const safeData = Array.isArray(data) ? data : [];

  // =====================================================
  // 🧮 OVERALL COUNTS (AUTHORITATIVE SOURCE)
  // =====================================================
  const overallExpected =
    Number(response_completion_summary?.total_expected_responses) || 0;

  const overallActual =
    Number(response_completion_summary?.overall_actual_response_ratio) || 0;

  const overallRatio =
    Number(response_completion_summary?.overall_actual_response_ratio) || 0;

  const surveyOverfilled = overallActual > overallExpected;
  const surveyOverBy = Math.max(0, overallActual - overallExpected);

  // =====================================================
  // 📊 TOTAL PERCENT (for waffle fill)
  // =====================================================
  const totalPercent = safeData.reduce(
    (sum, item) => sum + Number(item.percent_of_total_possible || 0),
    0
  );

  // Scale if exceeds 100
  const scaleFactor = totalPercent > 100 ? 100 / totalPercent : 1;

  // =====================================================
  // 🧇 BUILD ALLOCATIONS
  // =====================================================
  const allocations = safeData.map((item, index) => {
    const rawPercent = Number(item.percent_of_total_possible || 0);
    const scaledPercent = rawPercent * scaleFactor;

    const cells = Math.round((scaledPercent / 100) * TOTAL_CELLS);

    // department-wise counts (authoritative)
const deptCount =
  department_response_completion_summary?.find(
    d =>
      d.department === item.department &&
      d.level === item.level
  ) || {};

return {
  label: `${item.department} (${item.level})`,
  department: item.department,
  cells,
  percent: rawPercent.toFixed(1),
  color: COLORS[index % COLORS.length],

  // ✅ FIXED VALUES
  actual: Number(deptCount.actual_response_ratio || 0),
  // expected: Number(deptCount.total_questions || 0),
  // ratio: Number(deptCount.actual_response_ratio || 0)
  // Correct values from API
// actual: Number(deptCount.total_responses || 0),          // Count
expected: Number(deptCount.total_questions || 0),        // Expected questions count
ratio: Number(deptCount.actual_response_ratio || 0),     // Ratio for reference

};

  });
  // =====================================================
// 📌 DEPARTMENT TOTAL COUNTS (SUM OF ALL LEVELS)
// =====================================================
const departmentCounts = {};
allocations.forEach(a => {
  departmentCounts[a.department] =
    (departmentCounts[a.department] || 0) + Number(a.actual || 0);
});

  

//   allocations.forEach(a => {
//   departmentCounts[a.department] =
//     (departmentCounts[a.department] || 0) + Number(a.actual || 0);
// });

  // =====================================================
  // 🧇 BUILD CELL GRID
  // =====================================================
  let cellGrid = [];

  allocations.forEach(a => {
    for (let i = 0; i < a.cells; i++) {
      cellGrid.push({ label: a.label, color: a.color });
    }
  });

  cellGrid = cellGrid.slice(0, TOTAL_CELLS);

  while (cellGrid.length < TOTAL_CELLS) {
    cellGrid.push({ label: "Empty", color: NEUTRAL_COLOR });
  }

  // =====================================================
  // 📊 DEPARTMENT TOTALS
  // =====================================================
  const departmentTotals = {};
  allocations.forEach(a => {
    departmentTotals[a.department] =
      (departmentTotals[a.department] || 0) + Number(a.percent);
  });

  Object.keys(departmentTotals).forEach(
    d => (departmentTotals[d] = departmentTotals[d].toFixed(1))
  );

  // =====================================================
  // 🖼️ RENDER
  // =====================================================
  return (
    <div className="waffle-container">

      {/* 🧇 GRID */}
      <div
        className="waffle-grid"
        style={{
          gridTemplateColumns: `repeat(10, ${squareSize}px)`,
          gridTemplateRows: `repeat(10, ${squareSize}px)`
        }}
      >
        {cellGrid.map((cell, idx) => (
          <div
            key={idx}
            className="waffle-cell"
            title={cell.label}
            style={{
              width: squareSize,
              height: squareSize,
              background: cell.color
            }}
          />
        ))}
      </div>

      {/* 📊 OVERALL SUMMARY */}
      <div className={`waffle-total ${surveyOverfilled ? "danger" : ""}`}>
        <div>
          ✅ Total: <strong>{totalPercent.toFixed(1)}%</strong>
        </div>

        <div>
          📊 Actual: <strong>{overallActual}</strong> / Expected:{" "}
          <strong>{overallExpected}</strong>
        </div>

        {surveyOverfilled && (
          <div className="waffle-danger">
            ⚠ Survey overfilled by <strong>{surveyOverBy}</strong> responses
          </div>
        )}
      </div>

      {/* 🏷️ LEGEND */}
      <div className="waffle-legend">
        {allocations.map((item, idx) => {
          const isLastInDept =
            idx === allocations.length - 1 ||
            allocations[idx + 1].department !== item.department;

          const deptOver =
            item.actual > item.expected && item.expected > 0;

          return (
            <div key={idx} className="legend-row">
              <div className="legend-left">
                <span
                  className="legend-color"
                  style={{ background: item.color }}
                />
                <span className="legend-text">
                  {item.label} — {item.percent}%
                  {" "}(
                  {item.actual}
                  )
                </span>
              </div>

              {isLastInDept && (
                <div
                  className={`legend-right ${deptOver ? "danger" : ""}`}
                >
                  {item.department} — {departmentTotals[item.department]}%
                  {" "}(
                  {departmentCounts[item.department]}
                  )
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
